import { useEffect, useState } from "react";
import Box from "elements/Box";
import {
  Button,
  Chip,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";

import "assets/css/main.css";

import { ApiRequest } from "GlobalFunctions";
import BudgetAssign from "./BudgetAssign";
import BudgetHistory from "./BudgetHistory";

/*=========================================================
PURCHASES BUDGETS LIST - "Disponible"

Muestra los usuarios de la empresa con el dinero disponible que tienen
asignado actualmente y lo que les queda luego de sus compras. El botón +
permite asignar un nuevo valor, que pasa a ser el activo (el anterior se
conserva como histórico para trazabilidad).
=========================================================*/
export default function BudgetsList(props: any) {
  const { setLoading } = props;

  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [openAssign, setOpenAssign] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [activeUser, setActiveUser] = useState<any>(null);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedSearch]);

  const loadList = async () => {
    setLoading(true);

    await ApiRequest({
      url: "/purchases/budgets/list",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        page,
        filters: appliedSearch ? { name: appliedSearch } : {},
      },
      setResponse: (response: any) => {
        setList(response?.list || []);
        setTotalPages(response?.total_pages || 0);
        setLoading(false);
      },
    });
  };

  const currency = (value: any) =>
    `$${Number(value || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;

  const formatDate = (value: any) => {
    if (!value) return "-";
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("es-CO");
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  return (
    <div id="PurchasesBudgetsList" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Disponible</h2>
            <span className="h2_subtitle">Dinero disponible por usuario</span>
          </div>
          <div
            className="boxContainerActions"
            style={{ display: "flex", gap: 12, alignItems: "center" }}
          >
            <TextField
              size="small"
              label="Buscar usuario"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button variant="outlined" color="primary" onClick={handleSearch}>
              Buscar
            </Button>
          </div>
        </div>

        <Box id="PurchasesBudgetsListIn" className="box100">
          <TableContainer>
            <Table aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Usuario</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Correo</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>
                    Dinero disponible
                  </TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Consumido</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Restante</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>
                    Última asignación
                  </TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }} align="right">
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        No hay usuarios para mostrar.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {list.map((item, i) => (
                  <TableRow key={i} hover role="checkbox">
                    <TableCell>{item.user_name || "-"}</TableCell>
                    <TableCell>{item.email || "-"}</TableCell>
                    <TableCell>
                      {item.has_budget ? (
                        currency(item.amount)
                      ) : (
                        <Chip label="Sin asignar" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{item.has_budget ? currency(item.consumed) : "-"}</TableCell>
                    <TableCell>
                      {item.has_budget ? (
                        <b style={{ color: Number(item.available) < 0 ? "#d32f2f" : "#2e7d32" }}>
                          {currency(item.available)}
                        </b>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{item.has_budget ? formatDate(item.assigned_at) : "-"}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver historial">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setActiveUser(item);
                            setOpenHistory(true);
                          }}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Asignar nuevo dinero disponible">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setActiveUser(item);
                            setOpenAssign(true);
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            style={{ marginTop: "20px", alignSelf: "center" }}
          />
        </Box>
      </div>

      <BudgetAssign
        setLoading={setLoading}
        open={openAssign}
        setOpen={setOpenAssign}
        user={activeUser}
        loadList={loadList}
      />

      <BudgetHistory open={openHistory} setOpen={setOpenHistory} user={activeUser} />
    </div>
  );
}
